const STORAGE_KEY = "tars_impersonation_v2";
const LEGACY_STORAGE_KEY = "tars_superadmin_acting_user";
const DEFAULT_TARGET_ROLE = "user";

function normalizeRoleList(raw) {
  if (!Array.isArray(raw)) return [];
  return raw.map((item) => String(item ?? "").trim().toLowerCase()).filter(Boolean);
}

function normalizePayload(rawPayload) {
  if (!rawPayload || typeof rawPayload !== "object") return null;
  const payload = {
    realUserId: String(rawPayload.realUserId ?? "").trim(),
    realEmail: String(rawPayload.realEmail ?? "").trim(),
    targetUserId: String(rawPayload.targetUserId ?? "").trim(),
    targetEmail: String(rawPayload.targetEmail ?? "").trim(),
    targetDisplayName: String(rawPayload.targetDisplayName ?? "").trim(),
    targetRoles: normalizeRoleList(rawPayload.targetRoles ?? rawPayload.targetGroups),
    customerId: String(rawPayload.customerId ?? "").trim(),
  };
  if (!payload.targetUserId && !payload.targetEmail) return null;
  return payload;
}

function readKey(key) {
  if (typeof localStorage === "undefined") return null;
  const raw = localStorage.getItem(key);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return normalizePayload(parsed);
  } catch {
    return null;
  }
}

function readRawKey(key) {
  if (typeof localStorage === "undefined") return null;
  const raw = localStorage.getItem(key);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

function write(payload) {
  if (typeof localStorage === "undefined") return null;
  const normalized = normalizePayload(payload);
  if (!normalized) {
    clear();
    return null;
  }
  localStorage.setItem(STORAGE_KEY, JSON.stringify(normalized));
  return normalized;
}

function clear() {
  if (typeof localStorage === "undefined") return;
  localStorage.removeItem(STORAGE_KEY);
  localStorage.removeItem(LEGACY_STORAGE_KEY);
}

function read() {
  const current = readKey(STORAGE_KEY);
  if (current) return current;
  const legacy = readRawKey(LEGACY_STORAGE_KEY);
  if (!legacy) return null;
  const migrated = {
    realUserId: "",
    realEmail: "",
    targetUserId: String(legacy.userId ?? legacy.targetUserId ?? "").trim(),
    targetEmail: String(legacy.email ?? legacy.targetEmail ?? "").trim(),
    targetDisplayName: String(legacy.displayName ?? legacy.targetDisplayName ?? "").trim(),
    targetRoles: normalizeRoleList(legacy.roles ?? legacy.groups ?? legacy.targetRoles),
    customerId: String(legacy.customerId ?? "").trim(),
  };
  const normalized = write(migrated);
  localStorage.removeItem(LEGACY_STORAGE_KEY);
  return normalized;
}

function isActive() {
  return Boolean(read());
}

function validateAgainstCognito({ realUserId, realEmail, customerId }) {
  let payload = read();
  if (!payload) return null;
  const currentUser = String(realUserId ?? "").trim();
  const currentCustomer = String(customerId ?? "").trim();
  const payloadCustomer = String(payload.customerId ?? "").trim();
  if (!currentUser || !currentCustomer) {
    clear();
    return null;
  }
  if (!payloadCustomer) {
    clear();
    return null;
  }
  if (payloadCustomer !== currentCustomer) {
    clear();
    return null;
  }

  let payloadRealUser = String(payload.realUserId ?? "").trim();
  if (!payloadRealUser) {
    const repaired = write({
      ...payload,
      realUserId: currentUser,
      realEmail: String(realEmail ?? "").trim(),
    });
    if (!repaired) {
      clear();
      return null;
    }
    payload = repaired;
    payloadRealUser = currentUser;
  }

  if (payloadRealUser !== currentUser) {
    clear();
    return null;
  }
  return payload;
}

function start({ realUserId, realEmail, customerId, target }) {
  const targetUserId = String(target?.userId ?? target?.targetUserId ?? "").trim();
  const targetEmail = String(target?.email ?? target?.targetEmail ?? "").trim();
  if (!targetUserId && !targetEmail) return null;
  const targetRoles = normalizeRoleList(target?.roles ?? target?.groups ?? target?.targetRoles);
  return write({
    realUserId: String(realUserId ?? "").trim(),
    realEmail: String(realEmail ?? "").trim(),
    targetUserId,
    targetEmail,
    targetDisplayName: String(target?.displayName ?? target?.targetDisplayName ?? "").trim(),
    targetRoles: targetRoles.length > 0 ? targetRoles : [DEFAULT_TARGET_ROLE],
    customerId: String(customerId ?? "").trim(),
  });
}

function getPayload() {
  return read();
}

export {
  STORAGE_KEY,
  LEGACY_STORAGE_KEY,
  read,
  write,
  clear,
  isActive,
  validateAgainstCognito,
  start,
  getPayload,
};
